/**
 * Service Analytics - API pour statistiques Kiosk
 * Consomme les endpoints /api/analytics
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, delay, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LogService } from '../../services/log.service';

// ==================== INTERFACES ====================

export type PeriodType = 'today' | 'week' | 'month' | 'custom';
export type TrendType = 'up' | 'down' | 'stable';

export interface MetricEvolution {
  percentage: number;
  trend: TrendType;
}

export interface MetricWithEvolution {
  current: number;
  previous: number;
  evolution: MetricEvolution;
}

export interface SparklineData {
  date: string;
  revenue: number;
  transactions?: number;
}

export interface PerformanceOverview {
  period: PeriodType;
  dateRange: {
    start: string;
    end: string;
  };
  revenue: MetricWithEvolution;
  transactions: MetricWithEvolution;
  avgBasket: MetricWithEvolution;
  itemsSold: MetricWithEvolution;
  sparkline: SparklineData[];
}

export interface TopProduct {
  productId: string;
  productName: string;
  icon: string;
  quantitySold: number;
  revenue: number;
  percentOfTotal: number;
  trend: TrendType;
  trendPercentage: number;
}

export interface CategoryBreakdown {
  category: string;
  revenue: number;
  itemsSold: number;
  percentage: number;
}

export interface HourlyData {
  hour: number;
  salesCount: number;
  revenue: number;
  isPeak: boolean;
}

export interface HourlyActivity {
  hourly: HourlyData[];
  peakHours: number[];
  summary: {
    totalSales: number;
    totalRevenue: number;
    avgSalesPerHour: number;
  };
}

// ==================== INSIGHTS INTERFACES (Phase 3) ====================

export type InsightPriority = 'critical' | 'high' | 'medium' | 'low';

export interface InsightAction {
  label: string;
  route: string;
}

export interface Insight {
  id: string;
  type: string;
  priority: InsightPriority;
  icon: string;
  title: string;
  description: string;
  action: InsightAction | null;
  data: any;
}

export interface InsightsSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface InsightsResponse {
  insights: Insight[];
  summary: InsightsSummary;
  generatedAt: string;
}

// ==================== SERVICE ====================

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private http = inject(HttpClient);
  private log = inject(LogService);
  private apiUrl = `${environment.apiUrl}/analytics`;

  /**
   * Récupère l'aperçu de performance (KPIs principaux)
   */
  getPerformanceOverview(
    period: PeriodType = 'today',
    startDate?: Date,
    endDate?: Date,
  ): Observable<PerformanceOverview> {
    if (environment.mockApi) {
      return this.getMockPerformanceOverview(period);
    }

    let params: any = { period };
    if (period === 'custom' && startDate && endDate) {
      params.startDate = startDate.toISOString();
      params.endDate = endDate.toISOString();
    }

    return this.http
      .get<{
        success: boolean;
        data: PerformanceOverview;
      }>(`${this.apiUrl}/performance`, { params })
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.log.error('Erreur getPerformanceOverview', {
            error: (error as Error).message,
          });
          return this.getMockPerformanceOverview(period);
        }),
      );
  }

  /**
   * Récupère les top produits
   */
  getTopProducts(
    period: PeriodType = 'today',
    limit: number = 5,
    sortBy: 'revenue' | 'quantity' = 'revenue',
  ): Observable<TopProduct[]> {
    if (environment.mockApi) {
      return this.getMockTopProducts();
    }

    return this.http
      .get<{
        success: boolean;
        data: TopProduct[];
      }>(`${this.apiUrl}/products/top`, {
        params: { period, limit: limit.toString(), sortBy },
      })
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.log.error('Erreur getTopProducts', {
            error: (error as Error).message,
          });
          return this.getMockTopProducts();
        }),
      );
  }

  /**
   * Récupère la répartition par catégorie
   */
  getCategoryBreakdown(
    period: PeriodType = 'today',
  ): Observable<CategoryBreakdown[]> {
    if (environment.mockApi) {
      return this.getMockCategoryBreakdown();
    }

    return this.http
      .get<{
        success: boolean;
        data: CategoryBreakdown[];
      }>(`${this.apiUrl}/products/categories`, { params: { period } })
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.log.error('Erreur getCategoryBreakdown', {
            error: (error as Error).message,
          });
          return this.getMockCategoryBreakdown();
        }),
      );
  }

  /**
   * Récupère l'activité par heure
   */
  getHourlyActivity(date?: Date): Observable<HourlyActivity> {
    if (environment.mockApi) {
      return this.getMockHourlyActivity();
    }

    let params: any = {};
    if (date) {
      params.date = date.toISOString();
    }

    return this.http
      .get<{
        success: boolean;
        data: HourlyActivity;
      }>(`${this.apiUrl}/activity/hourly`, { params })
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.log.error('Erreur getHourlyActivity', {
            error: (error as Error).message,
          });
          return this.getMockHourlyActivity();
        }),
      );
  }

  /**
   * Récupère les insights et recommandations
   */
  getInsights(period: PeriodType = 'today'): Observable<InsightsResponse> {
    if (environment.mockApi) {
      return this.getMockInsights();
    }

    return this.http
      .get<{
        success: boolean;
        data: InsightsResponse;
      }>(`${this.apiUrl}/insights`, { params: { period } })
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.log.error('Erreur getInsights', {
            error: (error as Error).message,
          });
          return this.getMockInsights();
        }),
      );
  }

  // ==================== MOCK DATA ====================

  private getMockPerformanceOverview(
    period: PeriodType,
  ): Observable<PerformanceOverview> {
    const multiplier = period === 'week' ? 7 : period === 'month' ? 30 : 1;
    const baseRevenue = 450 * multiplier;
    const previousRevenue = 380 * multiplier;
    const revenueChange =
      ((baseRevenue - previousRevenue) / previousRevenue) * 100;

    const mockData: PerformanceOverview = {
      period,
      dateRange: {
        start: new Date().toISOString(),
        end: new Date().toISOString(),
      },
      revenue: {
        current: baseRevenue,
        previous: previousRevenue,
        evolution: {
          percentage: Math.round(revenueChange * 10) / 10,
          trend: 'up',
        },
      },
      transactions: {
        current: 25 * multiplier,
        previous: 20 * multiplier,
        evolution: { percentage: 25, trend: 'up' },
      },
      avgBasket: {
        current: 18,
        previous: 19,
        evolution: { percentage: -5.3, trend: 'down' },
      },
      itemsSold: {
        current: 80 * multiplier,
        previous: 65 * multiplier,
        evolution: { percentage: 23.1, trend: 'up' },
      },
      sparkline: this.generateMockSparkline(7),
    };

    return of(mockData).pipe(delay(300));
  }

  private getMockTopProducts(): Observable<TopProduct[]> {
    const mockProducts: TopProduct[] = [
      {
        productId: '1',
        productName: 'Burger Classic',
        icon: '🍔',
        quantitySold: 45,
        revenue: 540,
        percentOfTotal: 35,
        trend: 'up',
        trendPercentage: 12,
      },
      {
        productId: '2',
        productName: 'Frites Maison',
        icon: '🍟',
        quantitySold: 60,
        revenue: 240,
        percentOfTotal: 16,
        trend: 'stable',
        trendPercentage: 1,
      },
      {
        productId: '3',
        productName: 'Salade César',
        icon: '🥗',
        quantitySold: 25,
        revenue: 250,
        percentOfTotal: 16,
        trend: 'up',
        trendPercentage: 8,
      },
      {
        productId: '4',
        productName: 'Coca-Cola',
        icon: '🥤',
        quantitySold: 70,
        revenue: 210,
        percentOfTotal: 14,
        trend: 'down',
        trendPercentage: -5,
      },
      {
        productId: '5',
        productName: 'Brownie',
        icon: '🍫',
        quantitySold: 30,
        revenue: 150,
        percentOfTotal: 10,
        trend: 'up',
        trendPercentage: 20,
      },
    ];

    return of(mockProducts).pipe(delay(300));
  }

  private getMockCategoryBreakdown(): Observable<CategoryBreakdown[]> {
    const mockData: CategoryBreakdown[] = [
      { category: 'Plats', revenue: 790, itemsSold: 70, percentage: 51 },
      { category: 'Boissons', revenue: 310, itemsSold: 85, percentage: 20 },
      { category: 'Desserts', revenue: 250, itemsSold: 40, percentage: 16 },
      { category: 'Snacks', revenue: 200, itemsSold: 50, percentage: 13 },
    ];

    return of(mockData).pipe(delay(300));
  }

  private getMockHourlyActivity(): Observable<HourlyActivity> {
    const hourlyData: HourlyData[] = [];
    const peakHours = [12, 13, 19];

    for (let hour = 6; hour <= 22; hour++) {
      const isPeak = peakHours.includes(hour);
      hourlyData.push({
        hour,
        salesCount: isPeak
          ? 15 + Math.floor(Math.random() * 10)
          : 3 + Math.floor(Math.random() * 5),
        revenue: isPeak
          ? 300 + Math.floor(Math.random() * 100)
          : 50 + Math.floor(Math.random() * 50),
        isPeak,
      });
    }

    return of({
      hourly: hourlyData,
      peakHours,
      summary: {
        totalSales: hourlyData.reduce((sum, h) => sum + h.salesCount, 0),
        totalRevenue: hourlyData.reduce((sum, h) => sum + h.revenue, 0),
        avgSalesPerHour:
          hourlyData.reduce((sum, h) => sum + h.salesCount, 0) /
          hourlyData.length,
      },
    }).pipe(delay(300));
  }

  private generateMockSparkline(days: number): SparklineData[] {
    const data: SparklineData[] = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      data.push({
        date: date.toISOString().split('T')[0],
        revenue: 300 + Math.floor(Math.random() * 200),
        transactions: 15 + Math.floor(Math.random() * 15),
      });
    }

    return data;
  }

  private getMockInsights(): Observable<InsightsResponse> {
    // Simuler un utilisateur avec peu de données (onboarding)
    // Changer cette variable pour tester les différents états
    const mockSalesCount = 10; // 0 = pas de ventes, 1-4 = onboarding, 5+ = normal
    const mockHasProducts = true;

    // Cas onboarding
    if (mockSalesCount < 5) {
      return this.getMockOnboardingInsights(mockSalesCount, mockHasProducts);
    }

    // Cas normal (utilisateur établi)
    const insights: Insight[] = [
      {
        id: 'stock-low-1',
        type: 'stock_low',
        priority: 'high',
        icon: '⚠️',
        title: '3 produits en stock faible',
        description:
          'Pensez à réapprovisionner avant la prochaine période de pointe',
        action: {
          label: 'Gérer le stock',
          route: '/kiosk/stock',
        },
        data: { count: 3 },
      },
      {
        id: 'trending-up-1',
        type: 'trending_up',
        priority: 'low',
        icon: '🚀',
        title: 'Excellente performance !',
        description: '+18% de CA par rapport à la période précédente',
        action: null,
        data: { percentage: 18 },
      },
      {
        id: 'peak-hours-1',
        type: 'peak_hours',
        priority: 'low',
        icon: '⏰',
        title: 'Heures de pointe identifiées',
        description:
          "Vos pics d'activité : 12h, 13h, 19h. Assurez-vous d'être bien préparé !",
        action: null,
        data: { peakHours: [12, 13, 19] },
      },
      {
        id: 'top-performer-1',
        type: 'top_performer',
        priority: 'low',
        icon: '⭐',
        title: '"Burger Classic" cartonne !',
        description:
          "Ce produit représente 35% de votre CA. Assurez-vous d'avoir suffisamment de stock.",
        action: null,
        data: { productName: 'Burger Classic', percentOfTotal: 35 },
      },
    ];

    return of({
      insights,
      summary: {
        total: insights.length,
        critical: insights.filter((i) => i.priority === 'critical').length,
        high: insights.filter((i) => i.priority === 'high').length,
        medium: insights.filter((i) => i.priority === 'medium').length,
        low: insights.filter((i) => i.priority === 'low').length,
      },
      generatedAt: new Date().toISOString(),
    }).pipe(delay(400));
  }

  private getMockOnboardingInsights(
    salesCount: number,
    hasProducts: boolean,
  ): Observable<InsightsResponse> {
    const insights: Insight[] = [];

    if (!hasProducts) {
      // Pas de produits
      insights.push({
        id: 'onboarding-products-1',
        type: 'onboarding_add_products',
        priority: 'high',
        icon: '📦',
        title: 'Commencez par ajouter vos produits',
        description:
          "Pour utiliser le kiosk et suivre vos ventes, ajoutez d'abord votre catalogue produits.",
        action: {
          label: 'Ajouter des produits',
          route: '/kiosk/products',
        },
        data: {},
      });
      insights.push({
        id: 'onboarding-welcome-1',
        type: 'onboarding_welcome',
        priority: 'low',
        icon: '👋',
        title: 'Bienvenue sur votre tableau de bord !',
        description:
          'Cette page affichera vos statistiques et recommandations personnalisées dès que vous aurez quelques ventes.',
        action: null,
        data: {},
      });
    } else if (salesCount === 0) {
      // Produits mais pas de ventes
      insights.push({
        id: 'onboarding-first-sale-1',
        type: 'onboarding_first_sale',
        priority: 'medium',
        icon: '🎯',
        title: 'Prêt pour votre première vente !',
        description:
          'Vos produits sont configurés. Utilisez le kiosk pour enregistrer votre première vente.',
        action: {
          label: 'Accéder au kiosk',
          route: '/kiosk',
        },
        data: {},
      });
      insights.push({
        id: 'onboarding-stock-1',
        type: 'onboarding_configure_stock',
        priority: 'low',
        icon: '📊',
        title: 'Configurez vos seuils de stock',
        description:
          'Définissez un stock minimum pour chaque produit afin de recevoir des alertes.',
        action: {
          label: 'Gérer les stocks',
          route: '/kiosk/stock',
        },
        data: {},
      });
    } else {
      // Quelques ventes (1-4)
      insights.push({
        id: 'onboarding-progress-1',
        type: 'onboarding_keep_going',
        priority: 'low',
        icon: '🌱',
        title: `${salesCount} vente${salesCount > 1 ? 's' : ''} enregistrée${salesCount > 1 ? 's' : ''} !`,
        description:
          'Continuez ainsi ! Plus vous enregistrez de ventes, plus vos analyses seront précises.',
        action: null,
        data: { salesCount },
      });
      insights.push({
        id: 'onboarding-tip-1',
        type: 'onboarding_configure_stock',
        priority: 'low',
        icon: '💡',
        title: 'Conseil : anticipez vos besoins',
        description:
          "Après quelques jours d'activité, vous verrez apparaître vos heures de pointe et produits stars.",
        action: null,
        data: {},
      });
    }

    return of({
      insights,
      summary: {
        total: insights.length,
        critical: 0,
        high: insights.filter((i) => i.priority === 'high').length,
        medium: insights.filter((i) => i.priority === 'medium').length,
        low: insights.filter((i) => i.priority === 'low').length,
      },
      generatedAt: new Date().toISOString(),
    }).pipe(delay(400));
  }
}
